import React from 'react';
import ReactDOM from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import { logger } from './utils/logger';
import { isSupabaseAuthDomain } from './utils/runtimeEnvironment';
import { initWebVitals } from './utils/webVitals';
import App from './App.tsx';
import { ErrorBoundary } from './components/ErrorBoundary';
import './index.css';
import './styles/design-system.css';
import './polyfills';

// Sentry initialization is handled by monitoring service in App.tsx
// to avoid duplicate initialization and Session Replay conflicts

// Proactive stale session detection and clearing
// This runs before React mounts to prevent 401 loops from wrong Supabase project tokens
(function clearStaleSupabaseSession() {
  try {
    const expectedSupabaseRef = import.meta.env.VITE_SUPABASE_URL?.match(/\/\/([^.]+)\./)?.[1];
    if (!expectedSupabaseRef) return;

    // Scan ALL localStorage keys for potential Supabase tokens
    const allKeys = Object.keys(localStorage);
    for (const key of allKeys) {
      // Check any key that might contain Supabase auth data
      if (key.includes('supabase') || key.includes('vitly') || key.includes('sb-') || key.includes('auth')) {
        const stored = localStorage.getItem(key);
        if (stored) {
          try {
            const parsed = JSON.parse(stored);
            // Check various possible token locations in the stored object
            const accessToken = parsed?.access_token ||
                               parsed?.currentSession?.access_token ||
                               parsed?.session?.access_token ||
                               (typeof parsed === 'string' && parsed.includes('.') ? parsed : null);

            if (accessToken && typeof accessToken === 'string' && accessToken.includes('.')) {
              // Decode JWT payload to check project ref
              const parts = accessToken.split('.');
              if (parts.length >= 2) {
                const payloadBase64 = parts[1];
                const payload = JSON.parse(atob(payloadBase64));
                if (payload.ref && payload.ref !== expectedSupabaseRef) {
                  console.warn(`[Auth] Detected stale session from wrong Supabase project in key "${key}": ${payload.ref} (expected: ${expectedSupabaseRef})`);
                  // Clear this key
                  localStorage.removeItem(key);
                  sessionStorage.removeItem(key);
                }
              }
            }
          } catch {
            // If we can't parse it, check if it looks like a JWT with wrong ref
            if (stored.includes('wgqbfukckbyctkbfopet') && expectedSupabaseRef !== 'wgqbfukckbyctkbfopet') {
              console.warn(`[Auth] Removing key "${key}" containing production Supabase ref`);
              localStorage.removeItem(key);
              sessionStorage.removeItem(key);
            }
          }
        }
      }
    }

    // Also clear any cookies that might have stale tokens
    document.cookie.split(';').forEach(cookie => {
      const [name, value] = cookie.split('=').map(s => s.trim());
      if (name && (name.includes('supabase') || name.includes('vitly') || name.includes('sb-') || name.includes('auth'))) {
        // Check if cookie value contains production ref
        if (value && value.includes('wgqbfukckbyctkbfopet') && expectedSupabaseRef !== 'wgqbfukckbyctkbfopet') {
          console.warn(`[Auth] Clearing cookie "${name}" with production Supabase ref`);
          document.cookie = `${name}=; Path=/; Expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax`;
        }
      }
    });

    console.log('[Auth] Stale session check complete');
  } catch (e) {
    console.debug('[Auth] Error during stale session check:', e);
  }
})();

// Suppress Google Maps errors caused by tracking prevention
// These errors don't affect functionality but pollute the console
window.addEventListener('error', (event) => {
  const error = event.error || event.message || '';
  const errorStr = error.toString?.() || String(error);

  // Check if this is a Google Maps error from tracking prevention
  const isGoogleMapsError =
    event.filename?.includes('maps.googleapis.com') ||
    event.filename?.includes('common.js') ||
    event.filename?.includes('util.js') ||
    errorStr.includes('google is not defined') ||
    (errorStr.includes('Cannot read properties of undefined') && event.filename?.includes('main.js'));

  if (isGoogleMapsError) {
    // Prevent the error from being logged to console
    event.preventDefault();
    // Log a debug message instead
    logger.debug('[Maps] Tracking prevention blocking Google Maps resources (non-critical)');
    return;
  }
}, true);

initWebVitals();

const enableServiceWorker = import.meta.env.VITE_ENABLE_SERVICE_WORKER !== 'false';
const isAuthFlowRoute =
  typeof window !== 'undefined' &&
  (window.location.pathname.startsWith('/auth') ||
    window.location.pathname === '/login' ||
    window.location.pathname === '/register' ||
    window.location.pathname === '/forgot-password' ||
    window.location.pathname === '/reset-password');

if ('serviceWorker' in navigator) {
  const unregisterAllServiceWorkers = () => {
    navigator.serviceWorker.getRegistrations().then((registrations) => {
      registrations.forEach((registration) => {
        registration
          .unregister()
          .then((didUnregister) => {
            if (didUnregister) {
              logger.debug('[PWA] Unregistered service worker', registration.scope);
            }
          })
          .catch((error) => {
            console.warn('[PWA] Failed to unregister service worker', error);
          });
      });
    });
  };

  if (isSupabaseAuthDomain() || isAuthFlowRoute) {
    logger.debug('[PWA] Skipping service worker for auth/login context',
      isAuthFlowRoute ? { route: window.location.pathname } : undefined
    );
    unregisterAllServiceWorkers();
    if (navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({ type: 'SUPABASE_AUTH_CONTEXT' });
    }
  } else if (enableServiceWorker) {
    window.addEventListener('load', () => {
      navigator.serviceWorker
        .register('/service-worker.js')
        .then((registration) => {
          logger.debug('[PWA] Service Worker registered:', registration.scope);

          registration.addEventListener('updatefound', () => {
            const newWorker = registration.installing;
            if (newWorker) {
              newWorker.addEventListener('statechange', () => {
                if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                  logger.debug('[PWA] New content available, consider refreshing');
                }
              });
            }
          });
        })
        .catch((error) => {
          console.error('[PWA] Service Worker registration failed:', error);
        });
    });
  } else {
    logger.debug('[PWA] Service Worker disabled via environment flag, unregistering existing registrations');
    unregisterAllServiceWorkers();
  }
}

const rootElement = document.getElementById('root');

// Only use hydrateRoot for explicitly prerendered static snapshots (marketing pages).
// The main SPA shell (used for /login, /dashboard, etc. via rewrites) must always use createRoot
// to avoid React #418 hydration mismatches when the static HTML does not match the current route's UI.
if (!rootElement) {
  logger.error('[App] Root element not found; unable to mount React application');
} else if (
  typeof window !== 'undefined' &&
  window.__PRERENDERED__ === true &&
  rootElement.hasChildNodes()
) {
  ReactDOM.hydrateRoot(
    rootElement,
    <React.StrictMode>
      <HelmetProvider>
        <ErrorBoundary>
          <App />
        </ErrorBoundary>
      </HelmetProvider>
    </React.StrictMode>
  );
} else {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <React.StrictMode>
      <HelmetProvider>
        <ErrorBoundary>
          <App />
        </ErrorBoundary>
      </HelmetProvider>
    </React.StrictMode>,
  );
}
